import { t } from "i18next";
import { OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { DeviceGattApi } from "./DeviceGattApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";

function DeviceDataDisplay({ dataL, dataR }) {
    function getTemperatureText(data) {
        if (data && data.machineData.temperature !== null) {
            return data.machineData.temperature.toFixed(0) + "℃";
        } else {
            return t("device.data.na");
        }
    }

    function getTemperatureMeasurementText(data) {
        if (data && data.machineData.temperatureMeasurement !== null) {
            return data.machineData.temperatureMeasurement.toFixed(0) + "℃";
        } else {
            return t("device.data.na");
        }
    }

    function getFitnessMachineStatusText(data) {
        if (data && data.machineData.fitnessMachineStatus !== null) {
            return DeviceGattApi.getFitnessMachineStatusText(data.machineData.fitnessMachineStatus);
        } else {
            return t("device.data.na");
        }
    }

    function getFitnessMachineControlPointText(data) {
        if (data && data.machineData.fitnessMachineControlPoint !== null) {
            return DeviceGattApi.getFitnessMachineControlPointText(data.machineData.fitnessMachineControlPoint);
        } else {
            return t("device.data.na");
        }
    }

    function getHeatIndexText(data) {
        if (data && data.machineData.heatIndex !== null) {
            return DeviceGattApi.getHeatIndexText(data.machineData.heatIndex);
        } else {
            return t("device.data.na");
        }
    }

    function getElectricCurrentText(data) {
        if (data && data.machineData.electricCurrent !== null) {
            return data.machineData.electricCurrent.toFixed(2) + " " + t("A");
        } else {
            return t("device.data.na");
        }
    }

    function getVoltageText(data) {
        if (data && data.machineData.voltage !== null) {
            return data.machineData.voltage.toFixed(2) + " " + t("V");
        } else {
            return t("device.data.na");
        }
    }

    function getElectricPowerText(data) {
        if (data && data.machineData.power !== null) {
            return data.machineData.power.toFixed(2) + " " + t("W");
        } else {
            return t("device.data.na");
        }
    }

    function getDutyCycleText(data) {
        if (data && data.machineData.dutyCycle !== null) {
            return data.machineData.dutyCycle.toFixed(0) + "%";
        } else {
            return t("device.data.na");
        }
    }

    function getHighVoltageText(data) {
        if (data && data.machineData.highVoltage !== null) {
            return data.machineData.highVoltage.toFixed(2) + " " + t("V");
        } else {
            return t("device.data.na");
        }
    }

    function getResistanceText(data) {
        if (data && data.machineData.resistance !== null) {
            return data.machineData.resistance.toFixed(2) + " " + t("Ohm");
        } else {
            return t("device.data.na");
        }
    }

    function getBatteryLevelText(data) {
        if (data && data.deviceInfo.batteryLevel !== null) {
            return data.deviceInfo.batteryLevel + " %";
        } else {
            return t("device.data.na");
        }
    }

    function getEventStatisticsText(data) {
        if (data && typeof(data?.deviceInfo?.eventStatistics) === "object") {
            let lines = [];
            if (data.deviceInfo.eventStatistics?.execCounter !== undefined) {
                lines.push(`${t("device.data.execCounter")}: ${data.deviceInfo.eventStatistics.execCounter}`);
            }

            if (data.deviceInfo.eventStatistics?.errorCounter !== undefined) {
                lines.push(`${t("device.data.errorCounter")}: ${data.deviceInfo.eventStatistics.errorCounter}`);
            }

            if (data.deviceInfo.eventStatistics?.wdgResetCounter !== undefined) {
                lines.push(`${t("device.data.wdgResetCounter")}: ${data.deviceInfo.eventStatistics.wdgResetCounter}`);
            }

            if (data.deviceInfo.eventStatistics?.rporResetCounter !== undefined) {
                lines.push(`${t("device.data.rporResetCounter")}: ${data.deviceInfo.eventStatistics.rporResetCounter}`);
            }

            return lines.join("\n");
        } else {
            return t("device.data.na");
        }
    }

    function getManufacturerNameText(data) {
        if (data && data.deviceInfo.manufacturerName !== null) {
            return data.deviceInfo.manufacturerName;
        } else {
            return t("device.data.na");
        }
    }

    function getModelNumberText(data) {
        if (data && data.deviceInfo.modelNumber !== null) {
            return data.deviceInfo.modelNumber;
        } else {
            return t("device.data.na");
        }
    }

    function getSystemIdText(data) {
        if (data && data.deviceInfo.systemId !== null) {
            return data.deviceInfo.systemId;
        } else {
            return t("device.data.na");
        }
    }

    function getSerialNumberStringText(data) {
        if (data && data.deviceInfo.serialNumberString !== null) {
            return data.deviceInfo.serialNumberString;
        } else {
            return t("device.data.na");
        }
    }

    function getFirmwareRevisionStringText(data) {
        if (data && data.deviceInfo.firmwareRevisionString !== null) {
            return data.deviceInfo.firmwareRevisionString;
        } else {
            return t("device.data.na");
        }
    }

    function getHardwareRevisionStringText(data) {
        if (data && data.deviceInfo.hardwareRevisionString !== null) {
            return data.deviceInfo.hardwareRevisionString;
        } else {
            return t("device.data.na");
        }
    }

    return (
        <Table responsive striped bordered>
            <thead>
                <tr>
                    <th>{t("device.data.parameter")}</th>
                    <th>{t("device.data.left")}</th>
                    <th>{t("device.data.right")}</th>
                </tr>
            </thead>

            <tbody>
                <tr>
                    <td colSpan={3} className="fst-italic text-center">{t("device.data.fitnessMachine")}</td>
                </tr>
                <tr>
                    <td>{t("device.data.temperature")}</td>
                    <td>{getTemperatureText(dataL)}</td>
                    <td>{getTemperatureText(dataR)}</td>
                </tr>
                <tr>
                    <td>{t("device.data.temperatureMeasurement")}</td>
                    <td>{getTemperatureMeasurementText(dataL)}</td>
                    <td>{getTemperatureMeasurementText(dataR)}</td>
                </tr>
                <tr>
                    <td>{t("device.data.fitnessMachineStatus")}</td>
                    <td>{getFitnessMachineStatusText(dataL)}</td>
                    <td>{getFitnessMachineStatusText(dataR)}</td>
                </tr>
                <tr>
                    <td>{t("device.data.fitnessMachineControlPoint")}</td>
                    <td>{getFitnessMachineControlPointText(dataL)}</td>
                    <td>{getFitnessMachineControlPointText(dataR)}</td>
                </tr>
                <tr>
                    <td>{t("device.data.heatIndex")}</td>
                    <td>{getHeatIndexText(dataL)}</td>
                    <td>{getHeatIndexText(dataR)}</td>
                </tr>
                <tr>
                    <td>{t("device.data.electricCurrent")}</td>
                    <td>{getElectricCurrentText(dataL)}</td>
                    <td>{getElectricCurrentText(dataR)}</td>
                </tr>
                <tr>
                    <td>{t("device.data.voltage")}</td>
                    <td>{getVoltageText(dataL)}</td>
                    <td>{getVoltageText(dataR)}</td>
                </tr>
                <tr>
                    <td>{t("device.data.electricPower")}</td>
                    <td>{getElectricPowerText(dataL)}</td>
                    <td>{getElectricPowerText(dataR)}</td>
                </tr>
                <tr>
                    <td>{t("device.data.dutyCycle")}</td>
                    <td>{getDutyCycleText(dataL)}</td>
                    <td>{getDutyCycleText(dataR)}</td>
                </tr>
                <tr>
                    <td>{t("device.data.highVoltage")}</td>
                    <td>{getHighVoltageText(dataL)}</td>
                    <td>{getHighVoltageText(dataR)}</td>
                </tr>
                <tr>
                    <td>{t("device.data.resistance")}</td>
                    <td>{getResistanceText(dataL)}</td>
                    <td>{getResistanceText(dataR)}</td>
                </tr>

                <tr>
                    <td colSpan={3} className="fst-italic text-center">{t("device.data.deviceInfo")}</td>
                </tr>

                <tr>
                    <td>{t("device.data.batteryLevel")}</td>
                    <td>{getBatteryLevelText(dataL)}</td>
                    <td>{getBatteryLevelText(dataR)}</td>
                </tr>
                <tr>
                    <td>{t("device.data.eventStatistics")}</td>
                    <td style={{ whiteSpace: "pre-line" }}>{getEventStatisticsText(dataL)}</td>
                    <td style={{ whiteSpace: "pre-line" }}>{getEventStatisticsText(dataR)}</td>
                </tr>
                <tr>
                    <td>{t("device.data.manufacturerName")}</td>
                    <td>{getManufacturerNameText(dataL)}</td>
                    <td>{getManufacturerNameText(dataR)}</td>
                </tr>
                <tr>
                    <td>{t("device.data.modelNumber")}</td>
                    <td>{getModelNumberText(dataL)}</td>
                    <td>{getModelNumberText(dataR)}</td>
                </tr>
                <tr>
                    <td>{t("device.data.systemId")}</td>
                    <td>{getSystemIdText(dataL)}</td>
                    <td>{getSystemIdText(dataR)}</td>
                </tr>
                <tr>
                    <td className="text-muted">
                        {t("device.data.serialNumberString")} <OverlayTrigger
                            placement="auto"
                            delay={{ show: 250, hide: 400 }}
                            overlay={<Tooltip id="serialNumberStringHelp">{t("device.data.serialNumberStringHelp")}</Tooltip>}
                        >
                            <FontAwesomeIcon icon={faInfoCircle} size="xs" />
                        </OverlayTrigger>
                    </td>
                    <td className="text-muted">{getSerialNumberStringText(dataL)}</td>
                    <td className="text-muted">{getSerialNumberStringText(dataR)}</td>
                </tr>
                <tr>
                    <td>{t("device.data.firmwareRevisionString")}</td>
                    <td>{getFirmwareRevisionStringText(dataL)}</td>
                    <td>{getFirmwareRevisionStringText(dataR)}</td>
                </tr>
                <tr>
                    <td>{t("device.data.hardwareRevisionString")}</td>
                    <td>{getHardwareRevisionStringText(dataL)}</td>
                    <td>{getHardwareRevisionStringText(dataR)}</td>
                </tr>
            </tbody>
        </Table>
    );
}

export default DeviceDataDisplay;
